import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  CheckSquare,
  Calendar,
  Phone,
  RotateCcw,
  Package,
  Send,
  MapPin,
  FileText,
  ArrowRight,
} from "lucide-react";
import { fetchMyTasks } from "../lib/api";
import { Skeleton } from "./ui/skeleton";

const TYPE_META = {
  call_back:      { label: "Call back",      Icon: Phone,    cls: "text-emerald-300 border-emerald-500/30 bg-emerald-500/10" },
  follow_up:      { label: "Follow up",      Icon: RotateCcw, cls: "text-sky-300    border-sky-500/30    bg-sky-500/10" },
  send_sample:    { label: "Send sample",    Icon: Package,  cls: "text-amber-300  border-amber-500/30  bg-amber-500/10" },
  send_proposal:  { label: "Send proposal",  Icon: Send,     cls: "text-violet-300 border-violet-500/30 bg-violet-500/10" },
  schedule_visit: { label: "Schedule visit", Icon: MapPin,   cls: "text-pink-300   border-pink-500/30   bg-pink-500/10" },
  other:          { label: "Other",          Icon: FileText, cls: "text-zinc-300   border-zinc-700/40   bg-zinc-500/10" },
};

const TONE_CLS = {
  rose:    "text-rose-300    bg-rose-500/10    border-rose-500/30",
  amber:   "text-amber-300   bg-amber-500/10   border-amber-500/30",
  emerald: "text-emerald-300 bg-emerald-500/10 border-emerald-500/30",
  zinc:    "text-zinc-400    bg-zinc-500/10    border-zinc-700/40",
};

function formatDueDate(dueIso) {
  if (!dueIso) return null;
  const due = new Date(dueIso + "T00:00:00");
  if (Number.isNaN(due.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diff = Math.round((due - today) / 86400000);
  if (diff === 0) return { label: "Today",    tone: "amber" };
  if (diff === 1) return { label: "Tomorrow", tone: "amber" };
  if (diff < 0)   return { label: `${-diff}d overdue`, tone: "rose" };
  if (diff <= 7)  return {
    label: due.toLocaleDateString("en-US", { month: "short", day: "numeric" }),
    tone: "emerald",
  };
  return {
    label: due.toLocaleDateString("en-US", { month: "short", day: "numeric" }),
    tone: "zinc",
  };
}

/**
 * "My Open Tasks" — Dashboard tile for any signed-in user. Pulls
 * tasks where `assigned_to_id == me` so reps see their personal queue
 * at a glance. Quietly hides itself if nothing's assigned.
 */
export function MyOpenTasksCard({ limit = 6 }) {
  const [tasks, setTasks] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetchMyTasks({ limit })
      .then((data) => { if (!cancelled) setTasks(data); })
      .catch(() => { if (!cancelled) setTasks([]); });
    return () => { cancelled = true; };
  }, [limit]);

  if (tasks === null) {
    return (
      <div className="panel p-5" data-testid="my-open-tasks-card">
        <Skeleton className="h-32 rounded-md bg-zinc-900/50" />
      </div>
    );
  }

  const overdue = tasks.filter((t) => {
    const d = formatDueDate(t.due_date);
    return d?.tone === "rose";
  }).length;

  return (
    <div className="panel p-5" data-testid="my-open-tasks-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <CheckSquare className="w-4 h-4 text-emerald-400" />
          <h3 className="text-sm font-semibold tracking-tight text-zinc-100">
            My Open Tasks
          </h3>
          {tasks.length > 0 && (
            <span
              data-testid="my-tasks-count"
              className="text-[10px] uppercase tracking-wider font-mono px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"
            >
              {tasks.length}
            </span>
          )}
          {overdue > 0 && (
            <span
              data-testid="my-tasks-overdue"
              className="text-[10px] uppercase tracking-wider font-mono px-1.5 py-0.5 rounded bg-rose-500/15 text-rose-300 border border-rose-500/30"
            >
              {overdue} overdue
            </span>
          )}
        </div>
      </div>

      {tasks.length === 0 ? (
        <div className="text-xs text-zinc-500 italic py-3" data-testid="my-tasks-empty">
          Nothing on your plate. Tasks assigned to you on any dispensary will land here.
        </div>
      ) : (
        <ul className="space-y-1.5">
          {tasks.map((t) => {
            const meta = TYPE_META[t.task_type] || TYPE_META.other;
            const TypeIcon = meta.Icon;
            const due = formatDueDate(t.due_date);
            return (
              <li key={t.id}>
                <Link
                  to={`/dispensaries/${t.dispensary_id}`}
                  data-testid={`my-task-row-${t.id}`}
                  className="group flex items-start gap-3 rounded-md border border-zinc-800 bg-zinc-950/40 hover:border-zinc-700 hover:bg-zinc-900/60 px-3 py-2 transition-colors"
                >
                  <span
                    className={`shrink-0 mt-0.5 inline-flex items-center justify-center w-6 h-6 rounded border ${meta.cls}`}
                  >
                    <TypeIcon className="w-3 h-3" />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-zinc-100 truncate">{t.title}</div>
                    <div className="text-[11px] text-zinc-500 truncate">
                      {t.dispensary_name}
                      {t.dispensary_city ? ` · ${t.dispensary_city}` : ""}
                    </div>
                  </div>
                  {due && (
                    <span
                      className={`shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono uppercase tracking-wider border ${TONE_CLS[due.tone]}`}
                    >
                      <Calendar className="w-2.5 h-2.5" />
                      {due.label}
                    </span>
                  )}
                  <ArrowRight className="shrink-0 w-4 h-4 text-zinc-600 group-hover:text-zinc-300" />
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export default MyOpenTasksCard;
